import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Download, RefreshCw, Search } from "lucide-react";
import { CurrentHomeworkAssignment } from "../api/contracts";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  ActionsColumnHeader,
  ActivityTypePill,
  BooleanIndicator,
  ClientValuePill,
  PortalDataTable,
  PortalTableColumn,
} from "../components/DataDisplay";
import { ActivityStepsPreview } from "../components/ActivityStepsPreview";
import { ActionTooltip } from "../components/ActionTooltip";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  DeleteHomeworkDialog,
  RowActionMenu,
  TemplatePicker,
} from "../components/HomeworkControls";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { ClientPrimaryTabs } from "../components/PortalNavigation";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import { SingleSelectPillField } from "../components/SingleSelectPillField";
import {
  TableColumnsButton,
  usePortalTablePreferences,
} from "../components/TableColumnPreferences";
import { homeworkStepsEditorPath } from "../config/activityStepsNavigation";
import { downloadCsv, sanitizeCsvFilenamePart } from "../csvExport";
import {
  clientHomeworkDetailPath,
  clientWorkspacePath,
  homeworkDetailEditPath,
} from "../config/navigation";
import {
  getActivityTypePillToken,
  getHomeworkCompletionStatusPillToken,
  getHomeworkRepeatPillToken,
} from "../config/palette";
import { usePortalData } from "../hooks/usePortalData";
import { useActivityTemplates } from "../hooks/useActivityTemplates";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import { formatPortalDate } from "../utils/dateTime";
import {
  comparePortalDate,
  comparePortalNumber,
  comparePortalText,
  PortalSortDirection,
  stablePortalSort,
} from "../utils/tableSort";
import {
  readTeamClinicId,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

type CurrentHomeworkSortKey =
  | "overdue"
  | "dueDate"
  | "repeats"
  | "homeworkName"
  | "activityType"
  | "valueSupportedName"
  | "points"
  | "coins"
  | "assignedDate";

function homeworkRepeatLabel(assignment: CurrentHomeworkAssignment): string {
  return assignment.isRecurring && assignment.repeats
    ? assignment.repeats
    : "Does not repeat";
}

function mutationFailure(authorized: boolean, message: string) {
  return new ClinicianPortalApiError(
    message,
    authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
    authorized,
  );
}

export function CurrentHomeworkPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const [search, setSearch] = useState("");
  const [activityTypeFilter, setActivityTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState<CurrentHomeworkSortKey>("dueDate");
  const [sortDirection, setSortDirection] =
    useState<PortalSortDirection>("ascending");
  const [openTemplateAssignmentId, setOpenTemplateAssignmentId] = useState<
    string | null
  >(null);
  const [isAssignPickerOpen, setIsAssignPickerOpen] = useState(false);
  const [lastCreatedAssignmentId, setLastCreatedAssignmentId] = useState<
    string | null
  >(null);
  const [highlightedAssignmentId, setHighlightedAssignmentId] = useState<
    string | null
  >(null);
  const [deleteTarget, setDeleteTarget] =
    useState<CurrentHomeworkAssignment | null>(null);
  const [openActivityStepsId, setOpenActivityStepsId] = useState<string | null>(
    null,
  );
  const request = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getCurrentHomework(
            clinicianCode,
            patientId,
            signal,
            "team",
          )
        : clinicianPortalApi.getCurrentHomework(
            clinicianCode,
            patientId,
            signal,
          ),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const state = usePortalData(request);
  const activityTemplates = useActivityTemplates(clinicianCode, {
    enabled: !workspace.readOnly,
  });
  const snapshot = getClinicianPortalClientSnapshot(
    patientId,
    workspace.accessScope,
  );
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist && data.patient,
  );
  const patient = available ? data?.patient : snapshot?.patient;
  const therapist = available ? data?.therapist : snapshot?.therapist;
  const isMutating = mutation.state.status === "pending";
  const pendingAction = isMutating ? mutation.state.action : "";
  const scopedHomeworkDetailPath = (homeworkAssignmentId: string) =>
    workspace.source === "team"
      ? withTeamClinicScope(
          clientWorkspacePath(
            patientId,
            `current-homework/${homeworkAssignmentId}`,
            "team",
          ),
          readTeamClinicId(),
        )
      : clientHomeworkDetailPath(patientId, homeworkAssignmentId);
  const practiceCalendarPath =
    workspace.source === "team"
      ? withTeamClinicScope(
          clientWorkspacePath(patientId, "insights/practice-calendar", "team"),
          readTeamClinicId(),
        )
      : clientWorkspacePath(patientId, "insights/practice-calendar");
  const activityTypeOptions = useMemo(() => {
    const activityTypes = Array.from(
      new Set(
        (data?.assignments ?? [])
          .map((assignment) => assignment.activityType.trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { id: "all-activity-types", value: "", label: "All Activity Types" },
      ...activityTypes.map((activityType) => ({
        id: activityType,
        value: activityType,
        label: activityType,
      })),
    ];
  }, [data?.assignments]);
  const statusOptions = useMemo(() => {
    const statuses = Array.from(
      new Set(
        (data?.assignments ?? [])
          .map((assignment) => assignment.completionStatus.trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { id: "all-statuses", value: "", label: "All Statuses" },
      ...statuses.map((status) => ({
        id: status,
        value: status,
        label: status,
      })),
    ];
  }, [data?.assignments]);
  const matchingAssignments = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return (data?.assignments ?? []).filter((assignment) => {
      if (
        activityTypeFilter &&
        assignment.activityType.trim() !== activityTypeFilter
      ) {
        return false;
      }
      if (statusFilter && assignment.completionStatus.trim() !== statusFilter) {
        return false;
      }
      if (!query) return true;
      return [
        assignment.homeworkName,
        assignment.activityType,
        assignment.valueSupportedName,
        assignment.activitySteps,
        assignment.completionStatus,
        homeworkRepeatLabel(assignment),
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [activityTypeFilter, data?.assignments, search, statusFilter]);
  const sortedAssignments = useMemo(
    () =>
      stablePortalSort(matchingAssignments, (left, right) => {
        switch (sortKey) {
          case "overdue":
            return comparePortalNumber(
              Number(left.overdue),
              Number(right.overdue),
              sortDirection,
            );
          case "dueDate":
          case "assignedDate":
            return comparePortalDate(
              left[sortKey],
              right[sortKey],
              sortDirection,
            );
          case "points":
          case "coins":
            return comparePortalNumber(
              left[sortKey],
              right[sortKey],
              sortDirection,
            );
          case "repeats":
            return comparePortalText(
              homeworkRepeatLabel(left),
              homeworkRepeatLabel(right),
              sortDirection,
            );
          default:
            return comparePortalText(
              left[sortKey],
              right[sortKey],
              sortDirection,
            );
        }
      }),
    [matchingAssignments, sortDirection, sortKey],
  );

  const updateSort = (nextSortKey: CurrentHomeworkSortKey): void => {
    setSortDirection((currentDirection) =>
      nextSortKey === sortKey && currentDirection === "ascending"
        ? "descending"
        : "ascending",
    );
    setSortKey(nextSortKey);
    setOpenActivityStepsId(null);
  };

  const sortProps = (key: CurrentHomeworkSortKey) => ({
    sortDirection: sortKey === key ? sortDirection : ("none" as const),
    onSort: () => updateSort(key),
  });
  const totalAssignments = data?.assignments.length ?? 0;
  const hasActiveFilters = Boolean(
    search.trim() || activityTypeFilter || statusFilter,
  );
  const resultCount = hasActiveFilters
    ? `${matchingAssignments.length} of ${totalAssignments} ${
        totalAssignments === 1 ? "homework assignment" : "homework assignments"
      }`
    : `${totalAssignments} ${
        totalAssignments === 1 ? "homework assignment" : "homework assignments"
      }`;

  useEffect(() => {
    if (
      !highlightedAssignmentId ||
      !data?.assignments.some(
        (assignment) => assignment.id === highlightedAssignmentId,
      )
    ) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => setHighlightedAssignmentId(null),
      3500,
    );
    return () => window.clearTimeout(timeoutId);
  }, [data?.assignments, highlightedAssignmentId]);

  useEffect(() => {
    if (
      openActivityStepsId &&
      !matchingAssignments.some(
        (assignment) => assignment.id === openActivityStepsId,
      )
    ) {
      setOpenActivityStepsId(null);
    }
  }, [matchingAssignments, openActivityStepsId]);

  const refreshCurrentHomework = () => {
    mutation.reset();
    state.refresh();
  };

  const exportCurrentHomework = () => {
    const accessCode = patient?.accessCode || patientId;
    const exportedAt = new Date().toISOString().slice(0, 10);
    downloadCsv({
      filename: `${sanitizeCsvFilenamePart(accessCode)}-current-homework-${exportedAt}.csv`,
      headers: [
        "Due Date",
        "Repeats",
        "Homework Activity",
        "Activity Type",
        "Status",
        "Activity Steps",
        "Points",
        "Coins",
        "Value",
        "Assigned Date",
      ],
      rows: sortedAssignments.map((assignment) => [
        assignment.dueDate ? formatPortalDate(assignment.dueDate) : "",
        homeworkRepeatLabel(assignment),
        assignment.homeworkName,
        assignment.activityType,
        assignment.completionStatus,
        assignment.activitySteps,
        assignment.points,
        assignment.coins,
        assignment.valueSupportedName || "",
        formatPortalDate(assignment.assignedDate),
      ]),
    });
  };

  const createHomeworkFromTemplate = async (templateId: string) => {
    if (workspace.readOnly) return;
    let createdHomeworkId: string | null = null;
    const result = await mutation.run("assign", async (signal) => {
      const createResponse = await clinicianPortalApi.createHomework(
        clinicianCode,
        patientId,
        signal,
      );
      if (!createResponse.success || !createResponse.homework) {
        throw mutationFailure(
          createResponse.authorized,
          "The homework assignment could not be created.",
        );
      }
      createdHomeworkId = createResponse.homework.id;

      const selectResponse = await clinicianPortalApi.selectHomeworkTemplate(
        clinicianCode,
        patientId,
        createdHomeworkId,
        templateId,
        signal,
      );
      if (
        !selectResponse.success ||
        !selectResponse.homework ||
        !selectResponse.template
      ) {
        throw mutationFailure(
          selectResponse.authorized,
          "The activity template could not be selected.",
        );
      }
      return selectResponse;
    });

    if (!result?.homework) {
      if (createdHomeworkId) state.refresh();
      return;
    }
    setSearch("");
    setActivityTypeFilter("");
    setStatusFilter("");
    setLastCreatedAssignmentId(result.homework.id);
    setHighlightedAssignmentId(result.homework.id);
    state.refresh();
  };

  const selectTemplate = async (
    homeworkAssignmentId: string,
    templateId: string,
  ) => {
    if (workspace.readOnly) return;
    const result = await mutation.run(
      `select:${homeworkAssignmentId}`,
      async (signal) => {
        const response = await clinicianPortalApi.selectHomeworkTemplate(
          clinicianCode,
          patientId,
          homeworkAssignmentId,
          templateId,
          signal,
        );
        if (!response.success || !response.homework || !response.template) {
          throw mutationFailure(
            response.authorized,
            "The activity template could not be selected.",
          );
        }
        return response;
      },
    );

    if (!result) return;
    setOpenTemplateAssignmentId(null);
    state.refresh();
    navigateWithinPortal(
      withClinicianCode(
        scopedHomeworkDetailPath(homeworkAssignmentId),
        clinicianCode,
      ),
    );
  };

  const duplicateHomework = async (homeworkAssignmentId: string) => {
    if (workspace.readOnly) return;
    const result = await mutation.run(
      `duplicate:${homeworkAssignmentId}`,
      async (signal) => {
        const response = await clinicianPortalApi.duplicateHomework(
          clinicianCode,
          patientId,
          homeworkAssignmentId,
          signal,
        );
        if (!response.success || !response.duplicated || !response.homework) {
          throw mutationFailure(
            response.authorized,
            "The homework assignment could not be duplicated.",
          );
        }
        return response;
      },
    );

    if (result) state.refresh();
  };

  const deleteHomework = async () => {
    if (workspace.readOnly || !deleteTarget) return;
    const assignment = deleteTarget;
    const result = await mutation.run(
      `delete:${assignment.id}`,
      async (signal) => {
        const response = await clinicianPortalApi.deleteHomework(
          clinicianCode,
          patientId,
          assignment.id,
          signal,
        );
        if (
          !response.success ||
          !response.deleted ||
          response.homeworkAssignmentId !== assignment.id
        ) {
          throw mutationFailure(
            response.authorized,
            "The homework activity could not be deleted.",
          );
        }
        return response;
      },
    );

    if (result) {
      setDeleteTarget(null);
      state.refresh();
    }
  };

  const editableColumns: readonly PortalTableColumn<CurrentHomeworkAssignment>[] =
    [
      {
        key: "actions",
        header: <ActionsColumnHeader />,
        headerLabel: "Actions",
        initialWidth: 60,
        minWidth: 60,
        resizable: false,
        cellClassName: "cp-table-cell--compact",
        render: (assignment) => (
          <RowActionMenu
            clinicianCode={clinicianCode}
            editPath={homeworkDetailEditPath(
              scopedHomeworkDetailPath(assignment.id),
            )}
            activityName={assignment.homeworkName}
            canEdit={Boolean(assignment.homeworkName)}
            canDuplicate={Boolean(assignment.homeworkName) && !isMutating}
            isDuplicating={pendingAction === `duplicate:${assignment.id}`}
            onDuplicate={() => duplicateHomework(assignment.id)}
            canDelete={!isMutating}
            isDeleting={pendingAction === `delete:${assignment.id}`}
            onDelete={() => {
              mutation.reset();
              setDeleteTarget(assignment);
            }}
          />
        ),
      },
      {
        key: "overdue",
        header: "Overdue",
        initialWidth: 100,
        minWidth: 100,
        ...sortProps("overdue"),
        cellClassName: "cp-table-cell--compact",
        render: (assignment) => (
          <BooleanIndicator value={assignment.overdue} trueTone="danger" />
        ),
      },
      {
        key: "dueDate",
        header: "Due Date",
        initialWidth: 105,
        minWidth: 105,
        ...sortProps("dueDate"),
        cellClassName: "cp-table-cell--date-time",
        render: (assignment) => formatPortalDate(assignment.dueDate),
      },
      {
        key: "repeats",
        header: "Repeats",
        initialWidth: 140,
        minWidth: 125,
        ...sortProps("repeats"),
        cellClassName: "cp-table-cell--single-line",
        render: (assignment) => (
          <span
            className="cp-value-pill"
            data-palette={getHomeworkRepeatPillToken(assignment.repeats)}
          >
            {homeworkRepeatLabel(assignment)}
          </span>
        ),
      },
      {
        key: "homeworkName",
        header: "Homework Activity",
        initialWidth: 275,
        minWidth: 210,
        ...sortProps("homeworkName"),
        cellClassName: "cp-table-cell--single-line",
        render: (assignment) =>
          assignment.homeworkName ? (
            <span
              className="cp-homework-name cp-homework-activity-name"
              title={assignment.homeworkName}
            >
              {assignment.homeworkName}
            </span>
          ) : (
            <TemplatePicker
              templates={activityTemplates.templates}
              isOpen={openTemplateAssignmentId === assignment.id}
              isSelecting={pendingAction === `select:${assignment.id}`}
              isLoading={activityTemplates.isLoading}
              error={activityTemplates.error}
              onRetry={activityTemplates.retry}
              onOpenChange={(open) => {
                setOpenTemplateAssignmentId(open ? assignment.id : null);
              }}
              onSelect={(templateId) =>
                selectTemplate(assignment.id, templateId)
              }
            />
          ),
      },
      {
        key: "activityType",
        header: "Activity Type",
        initialWidth: 185,
        minWidth: 140,
        ...sortProps("activityType"),
        cellClassName: "cp-table-cell--single-line",
        render: (assignment) => (
          <ActivityTypePill
            activityType={assignment.activityType}
            title={assignment.activityType}
          />
        ),
      },
      {
        key: "activitySteps",
        header: "Activity Steps",
        initialWidth: 365,
        minWidth: 220,
        cellClassName: "cp-table-cell--single-line",
        render: (assignment) =>
          assignment.activitySteps ? (
            <ActivityStepsPreview
              activityName={assignment.homeworkName}
              activitySteps={assignment.activitySteps}
              isOpen={openActivityStepsId === assignment.id}
              onOpenChange={(open) =>
                setOpenActivityStepsId(open ? assignment.id : null)
              }
            />
          ) : (
            <span className="cp-empty-value">–</span>
          ),
      },
      {
        key: "editSteps",
        header: "Edit Steps",
        initialWidth: 125,
        minWidth: 110,
        resizable: false,
        render: (assignment) => (
          <a
            className="cp-edit-steps-button"
            href={homeworkStepsEditorPath({
              clinicianCode,
              patientId,
              homeworkAssignmentId: assignment.id,
              returnUrl: window.location.href,
            })}
          >
            Edit Steps
          </a>
        ),
      },
      {
        key: "points",
        header: "Points",
        initialWidth: 80,
        minWidth: 80,
        ...sortProps("points"),
        cellClassName: "cp-table-cell--compact",
        render: (assignment) => assignment.points,
      },
      {
        key: "coins",
        header: "Coins",
        initialWidth: 80,
        minWidth: 80,
        ...sortProps("coins"),
        cellClassName: "cp-table-cell--compact",
        render: (assignment) => assignment.coins,
      },
      {
        key: "valueSupportedName",
        header: "Value",
        initialWidth: 160,
        minWidth: 160,
        ...sortProps("valueSupportedName"),
        render: (assignment) => (
          <ClientValuePill value={assignment.valueSupportedName} />
        ),
      },
      {
        key: "assignedDate",
        header: "Assigned Date",
        initialWidth: 140,
        minWidth: 115,
        ...sortProps("assignedDate"),
        cellClassName: "cp-table-cell--date-time",
        render: (assignment) => formatPortalDate(assignment.assignedDate),
      },
    ];
  const columns = workspace.readOnly
    ? editableColumns.filter(
        (column) => column.key !== "actions" && column.key !== "editSteps",
      )
    : editableColumns;
  const tablePreference = usePortalTablePreferences({
    clinicianCode,
    tableKey: "current_homework",
    columns,
    rules: [
      {
        key: "actions",
        hideable: false,
        movable: false,
        position: "left",
      },
      { key: "homeworkName", hideable: false },
    ],
  });

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Current Homework",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="current-homework"
        />
      </PortalHeader>
      <div
        className="cp-page-content cp-page-content--wide cp-page-content--sticky-list cp-page-content--compact-client-list cp-current-homework"
        data-testid="current-homework-page"
        data-template-picker-open={
          openTemplateAssignmentId || isAssignPickerOpen ? "true" : undefined
        }
      >
        {state.status === "loading" ? (
          <LoadingState label="Loading current homework" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Current homework is not available")} />
        ) : null}
        {available ? (
          <section aria-labelledby="current-homework-heading">
            <div className="cp-content-header cp-content-header--route-intro">
              <div>
                <h2
                  id="current-homework-heading"
                  className="cp-visually-hidden"
                >
                  Current Homework
                </h2>
                <p>Not Completed assignments for this client.</p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>

            <div className="cp-list-toolbar cp-list-toolbar--homework">
              <div className="cp-list-toolbar__filters cp-list-toolbar__filters--homework">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">
                    Search current homework
                  </span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search current homework"
                    onChange={(event) => {
                      setOpenActivityStepsId(null);
                      setSearch(event.target.value);
                    }}
                  />
                </label>
                <label className="cp-homework-list-filter cp-homework-list-filter--activity-type">
                  <span className="cp-visually-hidden">
                    Filter by Activity Type
                  </span>
                  <SingleSelectPillField
                    id="current-homework-activity-type-filter"
                    value={activityTypeFilter}
                    options={activityTypeOptions}
                    ariaLabel="Filter by Activity Type"
                    placeholder="All Activity Types"
                    searchPlaceholder="Search Activity Types"
                    emptyMessage="No Activity Types found"
                    resolveColor={(option) =>
                      option.value
                        ? getActivityTypePillToken(option.label)
                        : "neutral"
                    }
                    onChange={(nextValue) => {
                      setOpenActivityStepsId(null);
                      setActivityTypeFilter(nextValue);
                    }}
                  />
                </label>
                <label className="cp-homework-list-filter cp-homework-list-filter--status">
                  <span className="cp-visually-hidden">Filter by Status</span>
                  <SingleSelectPillField
                    id="current-homework-status-filter"
                    value={statusFilter}
                    options={statusOptions}
                    ariaLabel="Filter by Status"
                    placeholder="All Statuses"
                    searchPlaceholder="Search Statuses"
                    emptyMessage="No Statuses found"
                    resolveColor={(option) =>
                      option.value
                        ? getHomeworkCompletionStatusPillToken(option.value)
                        : "neutral"
                    }
                    onChange={(nextValue) => {
                      setOpenActivityStepsId(null);
                      setStatusFilter(nextValue);
                    }}
                  />
                </label>
              </div>
              <div className="cp-content-header__actions cp-list-toolbar__actions">
                <ActionTooltip label="View Practice Calendar">
                  {(descriptionId) => (
                    <PortalLink
                      className="cp-button cp-button--secondary cp-button--icon-only"
                      clinicianCode={clinicianCode}
                      path={practiceCalendarPath}
                      aria-label="View Practice Calendar"
                      aria-describedby={descriptionId}
                    >
                      <CalendarDays aria-hidden="true" />
                    </PortalLink>
                  )}
                </ActionTooltip>
                <TableColumnsButton
                  preference={tablePreference}
                  label="Choose and reorder columns"
                />
                <ActionTooltip
                  label={state.status === "refreshing" ? "Refreshing homework…" : "Refresh homework"}
                >
                  {(descriptionId) => (
                    <button
                      type="button"
                      className="cp-button cp-button--secondary cp-button--icon-only"
                      aria-label={
                        state.status === "refreshing" ? "Refreshing homework…" : "Refresh homework"
                      }
                      aria-describedby={descriptionId}
                      data-loading={state.status === "refreshing" || undefined}
                      disabled={state.status === "refreshing" || isMutating}
                      onClick={refreshCurrentHomework}
                    >
                      <RefreshCw aria-hidden="true" />
                    </button>
                  )}
                </ActionTooltip>
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  disabled={
                    state.status === "refreshing" ||
                    isMutating ||
                    matchingAssignments.length === 0
                  }
                  onClick={exportCurrentHomework}
                >
                  <Download aria-hidden="true" />
                  Export CSV
                </button>
                {workspace.canManageHomework ? (
                  <TemplatePicker
                    templates={activityTemplates.templates}
                    isOpen={isAssignPickerOpen}
                    isSelecting={pendingAction === "assign"}
                    isDisabled={isMutating}
                    isLoading={activityTemplates.isLoading}
                    error={activityTemplates.error}
                    onRetry={activityTemplates.retry}
                    onOpenChange={(open) => {
                      if (open) mutation.reset();
                      setIsAssignPickerOpen(open);
                    }}
                    onSelect={createHomeworkFromTemplate}
                    triggerLabel="Add Activity"
                    selectingLabel="Adding activity…"
                    triggerVariant="primary"
                  />
                ) : null}
              </div>
            </div>

            {mutation.state.status === "error" &&
            !mutation.state.action.startsWith("delete:") ? (
              <div
                className="cp-inline-notice cp-inline-notice--error"
                role="alert"
              >
                <div>
                  <strong>Unable to complete that homework action.</strong>
                  <span>Please try again.</span>
                </div>
                <button type="button" onClick={mutation.reset}>
                  Dismiss
                </button>
              </div>
            ) : null}
            {mutation.state.status === "success" ? (
              <div className="cp-inline-notice" role="status">
                {mutation.state.action === "assign" ? (
                  <>
                    <span>Homework activity assigned.</span>
                    {lastCreatedAssignmentId ? (
                      <a
                        href={withClinicianCode(
                          homeworkDetailEditPath(
                            scopedHomeworkDetailPath(lastCreatedAssignmentId),
                          ),
                          clinicianCode,
                        )}
                      >
                        Edit details
                      </a>
                    ) : null}
                  </>
                ) : mutation.state.action.startsWith("select:") ? (
                  "Activity template selected."
                ) : mutation.state.action.startsWith("delete:") ? (
                  "Homework activity deleted."
                ) : (
                  "Homework assignment duplicated."
                )}
              </div>
            ) : null}

            {matchingAssignments.length > 0 ? (
              <PortalDataTable
                caption="Current Homework"
                density="compact"
                columns={tablePreference.visibleColumns}
                preferredColumnWidths={tablePreference.columnWidths}
                onColumnWidthsChange={tablePreference.updateColumnWidths}
                records={sortedAssignments}
                getRecordKey={(assignment) => assignment.id}
                getRecordOpenLabel={(assignment) =>
                  `Open ${assignment.homeworkName || "homework assignment"}`
                }
                isRecordOpenable={(assignment) =>
                  Boolean(assignment.homeworkName)
                }
                isRecordHighlighted={(assignment) =>
                  assignment.id === highlightedAssignmentId
                }
                onRecordOpen={(assignment) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      scopedHomeworkDetailPath(assignment.id),
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : (
              <div className="cp-current-homework__empty">
                <h3>
                  {data?.assignments.length
                    ? "No matching current homework"
                    : "No current homework"}
                </h3>
                <p>
                  {data?.assignments.length
                    ? "Try a different search or filter."
                    : "This client has no Not Completed homework assignments."}
                </p>
              </div>
            )}
            {workspace.canManageHomework ? (
              <DeleteHomeworkDialog
                homeworkName={deleteTarget?.homeworkName}
                isOpen={Boolean(deleteTarget)}
                isDeleting={Boolean(
                  deleteTarget &&
                    mutation.state.status === "pending" &&
                    mutation.state.action === `delete:${deleteTarget.id}`,
                )}
                hasError={Boolean(
                  deleteTarget &&
                    mutation.state.status === "error" &&
                    mutation.state.action === `delete:${deleteTarget.id}`,
                )}
                onCancel={() => {
                  setDeleteTarget(null);
                  mutation.reset();
                }}
                onConfirm={deleteHomework}
              />
            ) : null}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
